import React from 'react';
import { motion } from 'framer-motion';
import { Home, Building2, MapPin, Activity, Clock, Wifi } from 'lucide-react';
import { useWorkStore } from '../../store/work-buttons/workStore';
import { formatDistanceToNow } from 'date-fns';

export function StatusBar() {
  const { status, user } = useWorkStore();

  const locationIcon = status.location === 'home' ? <Home size={16} /> : <Building2 size={16} />;
  const locationLabel = status.location === 'home' ? 'Working from Home' : 'In Office';

  const availabilityColor = {
    available: 'bg-green-500',
    busy: 'bg-yellow-500',
    meeting: 'bg-red-500',
    break: 'bg-orange-500',
    focus: 'bg-purple-500',
    offline: 'bg-gray-500',
  }[status.availability];

  const availabilityLabel = {
    available: 'Available',
    busy: 'Busy',
    meeting: 'In Meeting',
    break: 'On Break',
    focus: 'Focus Mode',
    offline: 'Offline',
  }[status.availability];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6"
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-6">
          {/* Location Status */}
          <div className="flex items-center gap-2">
            <div className="text-blue-500">{locationIcon}</div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Location</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {locationLabel}
              </p>
            </div>
          </div>

          {/* Availability Status */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <div className={`w-3 h-3 rounded-full ${availabilityColor}`} />
              <div className={`absolute inset-0 w-3 h-3 rounded-full ${availabilityColor} animate-ping`} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {availabilityLabel}
              </p>
            </div>
          </div>

          {/* Department */}
          {user?.department && (
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Department</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user.department}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Last Updated */}
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Clock size={14} />
          <span>
            Updated {formatDistanceToNow(new Date(status.lastUpdated), { addSuffix: true })}
          </span>
        </div>
      </div>

      {/* Active Status Indicators */}
      <div className="mt-4 flex items-center gap-4">
        {status.availability === 'focus' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-3 py-1 rounded-full text-sm"
          >
            <Activity size={14} />
            <span>Focus Mode Active</span>
          </motion.div>
        )}
        
        {status.availability === 'meeting' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-3 py-1 rounded-full text-sm"
          >
            <Wifi size={14} className="animate-pulse" />
            <span>Currently in Meeting</span>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}